/**
 * /invitations/staff?token=: where a Lattice team invitation lands (MSG-036). The invited person
 * signs in with the invited email by code, then accepts. The role is theirs only if the signed-in
 * email is the invited one, which the Worker checks. Accepting sends them to set up an
 * authenticator, and from there into the admin app.
 *
 * A signed-in account with another email is told so and offered a sign-out, rather than a refusal
 * it cannot act on.
 */
import { useEffect, useState } from "react";
import { Button } from "@lattice/ui/Button";
import { acceptStaffInvitation, loadSession, signOut } from "./api.ts";
import { AuthLayout, authStyles } from "./AuthLayout.tsx";
import { EmailCodeSignIn } from "./EmailCodeSignIn.tsx";
import * as stylex from "@stylexjs/stylex";
type Stage = "loading" | "signed_out" | "ready";
const PROBLEMS = new Map([["wrong_email", "This invitation is for a different email address. Sign out, then sign in with the address it was sent to."], ["invitation_expired", "This invitation has expired. Ask whoever invited you for a new one."], ["not_found", "This invitation was already used or withdrawn. Ask for a new one if you still need access."]]);
function token(): string {
  return new URLSearchParams(window.location.search).get("token") ?? "";
}
function Accept() {
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  async function accept() {
    setBusy(true);
    setProblem(null);
    const result = await acceptStaffInvitation(token());
    if (result.status === "ok") {
      window.location.assign(`/security/setup?next=${encodeURIComponent("/admin/")}`);
      return;
    }
    setBusy(false);
    setProblem(PROBLEMS.get(result.code) ?? "We couldn't accept the invitation. Try again.");
  }
  return <div {...stylex.props(authStyles.form)}>
      <Button busy={busy} testId="accept-staff-invitation" onClick={() => void accept()}>
        Accept the invitation
      </Button>
      {problem === null ? null : <p role="alert" {...stylex.props(authStyles.error)}>
          {problem}
        </p>}
      {problem === PROBLEMS.get("wrong_email") ? <Button tone="quiet" onClick={() => void signOut().then(() => window.location.reload())}>
          Sign out
        </Button> : null}
    </div>;
}
export function StaffInvitation() {
  const [stage, setStage] = useState<Stage>("loading");
  useEffect(() => {
    document.title = "Join the Lattice team · Lattice";
    void loadSession().then(loaded => setStage(loaded.status === "ok" && !loaded.me.anonymous ? "ready" : "signed_out"));
  }, []);
  if (stage === "loading") return <AuthLayout title="Join the Lattice team" lead="Checking your invitation.">
        <div aria-busy="true" />
      </AuthLayout>;
  if (stage === "signed_out") return <AuthLayout title="Join the Lattice team" lead="Sign in with the email address the invitation was sent to. We'll send a six-digit code.">
        <EmailCodeSignIn next={window.location.pathname + window.location.search} />
      </AuthLayout>;
  return <AuthLayout title="Join the Lattice team" lead="Accept to take up the role you were invited to. You'll set up an authenticator app next.">
      <Accept />
    </AuthLayout>;
}